'use client';

import { useEffect, useState } from 'react';

interface CountdownTime {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isExpired: boolean;
}

const INITIAL_COUNTDOWN: CountdownTime = {
  days: 0,
  hours: 0,
  minutes: 0,
  seconds: 0,
  isExpired: true,
};

export interface AuctionCountdownResult extends CountdownTime {
  /** False until the first calculation has run (e.g. after effect runs). Use to show loader. */
  isReady: boolean;
}

/**
 * Custom hook to calculate countdown time from a target date
 * Updates every second to provide real-time countdown
 */
export function useAuctionCountdown(
  targetDate: Date | string | null | undefined,
): AuctionCountdownResult {
  const [timeLeft, setTimeLeft] = useState<CountdownTime>(INITIAL_COUNTDOWN);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    setIsReady(false);

    if (!targetDate) {
      setTimeLeft(INITIAL_COUNTDOWN);
      setIsReady(true);
      return;
    }

    const calculateTimeLeft = (): CountdownTime => {
      const target = new Date(targetDate).getTime();
      const now = new Date().getTime();
      const difference = target - now;

      if (difference <= 0) {
        return {
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          isExpired: true,
        };
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
      );
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      return {
        days,
        hours,
        minutes,
        seconds,
        isExpired: false,
      };
    };

    // First calculation – marks countdown as ready
    setTimeLeft(calculateTimeLeft());
    setIsReady(true);

    // Update every second
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    // When the screen is locked or tab is hidden, browsers suspend setInterval.
    // On return, immediately recalculate so the countdown doesn't appear frozen.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setTimeLeft(calculateTimeLeft());
      }
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      clearInterval(timer);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };
  }, [targetDate]);

  return { ...timeLeft, isReady };
}

/**
 * Format countdown time for display
 * Returns formatted string like "2h:01m:48s" or "00:24hr:32m"
 */
export function formatCountdownTime(time: CountdownTime): string {
  const { days, hours, minutes, seconds } = time;

  if (days > 0) {
    return `${days}d:${String(hours).padStart(2, '0')}h:${String(minutes).padStart(2, '0')}m`;
  }

  return `${hours}h:${String(minutes).padStart(2, '0')}m:${String(seconds).padStart(2, '0')}s`;
}
